import type {
	OrderCheckFiringLevel,
	OrderCheckLevel,
} from './order-check-level';
import { isOrderCheckOn, isOrderedQuantity } from './order-check-level';

/**
 * PD-16276 — per-order, daily and monthly quantity limits.
 *
 * A limit of 0 or less (or none at all) means "no limit". The CRM cart and the
 * CRM submit always read it that way; the storefront engine used to treat 0
 * as "nothing may be ordered", which is now reconciled to the same rule.
 */

export interface QuantityLimitLineModel {
	itemId: number;
	quantity: number;
	/** The effective limit for the line. Null, undefined, 0 or less is no limit. */
	limit: number | null | undefined;
}

export interface PerOrderLimitFindingModel {
	itemId: number;
	level: OrderCheckFiringLevel;
	limit: number;
	quantity: number;
}

/** A single order may not carry more of an item than its per-order limit. */
export function checkPerOrderLimits(
	lines: ReadonlyArray<QuantityLimitLineModel>,
	level: OrderCheckLevel | null | undefined,
): Array<PerOrderLimitFindingModel> {
	if (!isOrderCheckOn(level)) {
		return [];
	}

	const findings: Array<PerOrderLimitFindingModel> = [];

	for (const line of lines) {
		const limit = line.limit;

		if (!isOrderedQuantity(line.quantity) || limit == null || limit <= 0) {
			continue;
		}

		if (line.quantity > limit) {
			findings.push({
				itemId: line.itemId,
				level,
				limit,
				quantity: line.quantity,
			});
		}
	}

	return findings;
}

export interface PeriodQuantityLimitLineModel extends QuantityLimitLineModel {
	/**
	 * What the client already ordered of the item in the period, on orders
	 * other than this one. The caller owns the period (today for the daily
	 * limit, the rolling last 30 days for the monthly one).
	 */
	alreadyOrdered: number;
}

export interface PeriodQuantityLimitFindingModel {
	itemId: number;
	level: OrderCheckFiringLevel;
	limit: number;
	quantity: number;
	alreadyOrdered: number;
	/** How many more the client could still order in the period (may be negative). */
	remaining: number;
}

/**
 * Daily and monthly limits share one rule: this order plus what was already
 * ordered in the period may not go over the limit.
 */
export function checkPeriodQuantityLimits(
	lines: ReadonlyArray<PeriodQuantityLimitLineModel>,
	level: OrderCheckLevel | null | undefined,
): Array<PeriodQuantityLimitFindingModel> {
	if (!isOrderCheckOn(level)) {
		return [];
	}

	const findings: Array<PeriodQuantityLimitFindingModel> = [];

	for (const line of lines) {
		const limit = line.limit;

		if (!isOrderedQuantity(line.quantity) || limit == null || limit <= 0) {
			continue;
		}

		const alreadyOrdered = Number.isFinite(line.alreadyOrdered)
			? line.alreadyOrdered
			: 0;

		if (line.quantity + alreadyOrdered > limit) {
			findings.push({
				itemId: line.itemId,
				level,
				limit,
				quantity: line.quantity,
				alreadyOrdered,
				remaining: limit - alreadyOrdered,
			});
		}
	}

	return findings;
}
